import type { JobStatus } from "@zenforge/shared";
import { useEffect, useState } from "react";
import { api, uploadFile, useApi } from "../api.js";
import { Notice, StatusBadge, useToast } from "../components.js";
import { useStickyState } from "../useStickyState.js";

interface Named {
  id: string;
  name: string;
}

interface ChapterNode {
  id: string;
  name: string;
  sequenceNo: number | null;
}

interface ChapterTree {
  id: string;
  board: string;
  class: string;
  subject: string;
  chapters: ChapterNode[];
}

interface CollectionRow {
  id: number;
  name: string;
  model: string;
  vectorStoreId: string | null;
  syllabusId: string | null;
  board: string | null;
  className: string | null;
  subject: string | null;
}

interface CategoryRow {
  id: number;
  name: string;
  code: string;
  categoryType?: "MODULE" | "BOOK_IN" | "BOOK_BACK";
  questionAssessmentType?: string | null;
  questionCategoryId?: string | null;
  questionMark?: number | null;
  responseType: "html" | "json" | "markdown";
  isActive: boolean;
}

interface KnowledgeSourceRow {
  id: number;
  filename: string;
  roleTag: "BOOK_BACK_QUESTIONS" | "CHAPTER_GUIDE" | "TEXTBOOK";
  indexingStatus: "pending" | "uploading" | "indexing" | "ready" | "failed";
  scopes?: { chapterId: string }[];
}

interface ExistingBookBackContent {
  id: number;
  categoryId: number;
  categoryName: string;
  status: JobStatus;
  missingGuideAnswer: boolean;
  pushStatus: string | null;
  reviewedByUserName: string | null;
  updatedAt: string;
}

/**
 * Dedicated Book-Back Questions Generation Screen
 * 
 * Provides a specialized, streamlined workflow for generating textbook end-of-chapter
 * book-back questions and solutions in a single batch.
 */
export function BookBackGeneration() {
  const [board, setBoard] = useStickyState<string | null>("zenforge:bbg:board", null);
  const [className, setClassName] = useStickyState<string | null>("zenforge:bbg:class", null);
  const [subject, setSubject] = useStickyState<string | null>("zenforge:bbg:subject", null);
  const [chapterId, setChapterId] = useState<string | null>(null);

  const [selectedCategories, setSelectedCategories] = useState<Set<number>>(new Set());
  const [autoApprove, setAutoApprove] = useState(false);
  const [modelOverride, setModelOverride] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Quick PDF Upload state
  const [uploadingRole, setUploadingRole] = useState<"BOOK_BACK_QUESTIONS" | "CHAPTER_GUIDE" | null>(null);

  const { toast, element: toastElement } = useToast();

  const boards = useApi<Named[]>("/boards");
  const classes = useApi<Named[]>("/classes");
  const subjects = useApi<Named[]>("/subjects");
  const models = useApi<string[]>("/settings/models");

  const queryParams = new URLSearchParams();
  if (board) queryParams.set("board", board);
  if (className) queryParams.set("class", className);
  if (subject) queryParams.set("subject", subject);

  const tree = useApi<ChapterTree>(
    board && className && subject ? `/chapter-tree?${queryParams.toString()}` : null,
    [board, className, subject],
  );

  const collections = useApi<CollectionRow[]>("/collections");
  const collection = collections.data?.find(
    (c) => c.board === board && c.className === className && c.subject === subject,
  );
  const collectionId = collection?.id ?? null;

  const categories = useApi<CategoryRow[]>(
    collectionId ? `/collections/${collectionId}/categories` : null,
    [collectionId],
  );

  const knowledgeSources = useApi<KnowledgeSourceRow[]>(
    collectionId ? `/collections/${collectionId}/knowledge-sources` : null,
    [collectionId, refreshKey],
  );

  // Existing book-back content for selected chapter
  const existingContent = useApi<ExistingBookBackContent[]>(
    chapterId ? `/generated-contents?chapterId=${chapterId}` : null,
    [chapterId, refreshKey],
  );

  const bookBackCategories = (categories.data ?? []).filter(
    (c) => (c.categoryType ?? (c.questionCategoryId ? "BOOK_BACK" : c.questionAssessmentType ? "BOOK_IN" : "MODULE")) === "BOOK_BACK",
  );

  // Auto-select Book-Back categories
  useEffect(() => {
    setSelectedCategories(new Set(bookBackCategories.map((c) => c.id)));
  }, [categories.data]);

  const chapterPDFs = knowledgeSources.data?.filter(
    (ks) => ks.scopes?.some((s) => s.chapterId === chapterId),
  ) ?? [];

  const questionsPDF = chapterPDFs.find((ks) => ks.roleTag === "BOOK_BACK_QUESTIONS");
  const guidePDF = chapterPDFs.find((ks) => ks.roleTag === "CHAPTER_GUIDE");

  const toggleCategory = (id: number) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleQuickUpload = async (e: React.ChangeEvent<HTMLInputElement>, role: "BOOK_BACK_QUESTIONS" | "CHAPTER_GUIDE") => {
    const file = e.target.files?.[0];
    if (!file || !collectionId || !chapterId) return;
    if (file.type !== "application/pdf") {
      toast("Only PDF files are accepted");
      return;
    }

    setUploadingRole(role);
    try {
      await uploadFile(`/collections/${collectionId}/knowledge-sources`, file, {
        chapterId,
        roleTag: role,
      });
      toast(`${file.name} uploaded as ${role === "BOOK_BACK_QUESTIONS" ? "Book-Back Questions PDF" : "Chapter Guide PDF"}`);
      setRefreshKey((k) => k + 1);
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Upload failed");
    } finally {
      setUploadingRole(null);
    }
  };

  const handleLaunchBookBackBatch = async () => {
    if (!collectionId) {
      toast("Choose a collection first");
      return;
    }
    if (!chapterId) {
      toast("Select a chapter to generate book-back questions");
      return;
    }
    if (selectedCategories.size === 0) {
      toast("Select at least one category to generate");
      return;
    }

    setSubmitting(true);
    try {
      const result = await api.post<{ created: number; skipped: number; skippedApproved: number }>(
        "/generation-jobs/book-back-chapter",
        {
          collectionId,
          chapterId,
          categoryIds: [...selectedCategories],
          autoApprove,
          model: modelOverride,
        },
      );
      toast(`🚀 Single-batch Book-Back generation launched (${result.created} category jobs queued)`);
      setRefreshKey((k) => k + 1);
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Failed to launch book-back generation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="book-back-generation-page">
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="row between">
          <div>
            <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600 }}>
              📖 Dedicated Book-Back Questions Generation
            </h2>
            <div className="mini muted" style={{ marginTop: 2 }}>
              Generate, pair, and digitize official textbook end-of-chapter questions & guide solutions
            </div>
          </div>
        </div>

        {/* Global Academic Hierarchy Selection */}
        <div className="row" style={{ gap: 12, marginTop: 14, flexWrap: "wrap" }}>
          <select value={board ?? ""} onChange={(e) => setBoard(e.target.value || null)}>
            <option value="">Select Board…</option>
            {boards.data?.map((b) => (
              <option key={b.id} value={b.name}>{b.name}</option>
            ))}
          </select>

          <select value={className ?? ""} onChange={(e) => setClassName(e.target.value || null)}>
            <option value="">Select Class…</option>
            {classes.data?.map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>

          <select value={subject ?? ""} onChange={(e) => setSubject(e.target.value || null)}>
            <option value="">Select Subject…</option>
            {subjects.data?.map((s) => (
              <option key={s.id} value={s.name}>{s.name}</option>
            ))}
          </select>

          <select
            value={chapterId ?? ""}
            disabled={!tree.data || tree.data.chapters.length === 0}
            onChange={(e) => setChapterId(e.target.value || null)}
            style={{ flex: 1, minWidth: 220 }}
          >
            <option value="">Select Chapter Target…</option>
            {tree.data?.chapters.map((c) => (
              <option key={c.id} value={c.id}>
                {c.sequenceNo ? `${c.sequenceNo}. ` : ""}{c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!chapterId ? (
        <Notice kind="info">Select a Board, Class, Subject, and Chapter Target above to open the Book-Back Generation studio.</Notice>
      ) : (
        <div className="grid" style={{ gap: 20 }}>
          {/* Left Column: Knowledge PDFs & Categories Selection */}
          <div className="card span-7">
            <h3>1. Chapter Knowledge Input PDFs</h3>
            <p className="mini muted">
              Book-Back generation pairs questions from the un-answered PDF with solutions from the Chapter Guide PDF.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
              {/* Question PDF Card */}
              <div
                style={{
                  padding: "14px 16px",
                  borderRadius: 6,
                  border: "1px solid var(--border)",
                  borderLeft: questionsPDF ? "5px solid var(--primary)" : "5px solid var(--amber)",
                  backgroundColor: "var(--surface)",
                }}
              >
                <div className="row between" style={{ gap: 12, alignItems: "center" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>📖 Book-Back Questions PDF</div>
                    <div className="mini muted" style={{ marginTop: 2, wordBreak: "break-word" }}>
                      {questionsPDF ? questionsPDF.filename : "No Book-Back Questions PDF uploaded for this chapter"}
                    </div>
                  </div>
                  <label className="btn mini" style={{ cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
                    {uploadingRole === "BOOK_BACK_QUESTIONS" ? "Uploading…" : questionsPDF ? "Replace PDF" : "+ Upload Questions PDF"}
                    <input
                      type="file"
                      accept="application/pdf"
                      style={{ display: "none" }}
                      onChange={(e) => void handleQuickUpload(e, "BOOK_BACK_QUESTIONS")}
                      disabled={uploadingRole !== null}
                    />
                  </label>
                </div>
              </div>

              {/* Guide PDF Card */}
              <div
                style={{
                  padding: "14px 16px",
                  borderRadius: 6,
                  border: "1px solid var(--border)",
                  borderLeft: guidePDF ? "5px solid var(--emerald)" : "5px solid var(--amber)",
                  backgroundColor: "var(--surface)",
                }}
              >
                <div className="row between" style={{ gap: 12, alignItems: "center" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>💡 Chapter Guide PDF (With Solutions)</div>
                    <div className="mini muted" style={{ marginTop: 2, wordBreak: "break-word" }}>
                      {guidePDF ? guidePDF.filename : "No Chapter Guide PDF uploaded for this chapter"}
                    </div>
                  </div>
                  <label className="btn mini" style={{ cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
                    {uploadingRole === "CHAPTER_GUIDE" ? "Uploading…" : guidePDF ? "Replace PDF" : "+ Upload Guide PDF"}
                    <input
                      type="file"
                      accept="application/pdf"
                      style={{ display: "none" }}
                      onChange={(e) => void handleQuickUpload(e, "CHAPTER_GUIDE")}
                      disabled={uploadingRole !== null}
                    />
                  </label>
                </div>
              </div>
            </div>

            <h3>2. Select Book-Back Categories</h3>
            {bookBackCategories.length === 0 ? (
              <p className="mini muted">No active Book-Back categories found in this collection. Add one under Collection -&gt; Categories -&gt; Book-Back.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                {bookBackCategories.map((cat) => (
                  <label
                    key={cat.id}
                    className="row between"
                    style={{
                      padding: "10px 14px",
                      border: "1px solid var(--border)",
                      borderRadius: 6,
                      cursor: "pointer",
                      backgroundColor: selectedCategories.has(cat.id) ? "var(--surface-hover)" : "var(--surface)",
                    }}
                  >
                    <div className="row" style={{ gap: 8, alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={selectedCategories.has(cat.id)}
                        onChange={() => toggleCategory(cat.id)}
                      />
                      <strong>{cat.name}</strong>
                      {/* <span className="badge neutral mini">{cat.code}</span> */}
                      {/* {cat.questionAssessmentType && (
                        <span className="badge info mini">Question Bank: {cat.questionAssessmentType}</span>
                      )} */}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Right Column: Execution Configuration & Single-Batch Launch */}
          <div className="card span-5">
            <h3>3. Execution Options</h3>

            <div className="field">
              <label>AI Model Override</label>
              <select
                value={modelOverride ?? ""}
                onChange={(e) => setModelOverride(e.target.value || null)}
              >
                <option value="">Collection Default ({collection?.model ?? "gpt-4o"})</option>
                {models.data?.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="row" style={{ gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={autoApprove}
                  onChange={(e) => setAutoApprove(e.target.checked)}
                />
                <span>
                  <strong>Auto-Approve & Push</strong>
                  <div className="mini muted">
                    Pushes generated book-back questions directly to main app (`source: book_back`) upon worker completion.
                  </div>
                </span>
              </label>
            </div>

            <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
              <div className="mini muted" style={{ marginBottom: 10 }}>
                Single Batch Execution: 1 Chapter × {selectedCategories.size} selected category/categories
              </div>
              <button
                type="button"
                className="btn primary"
                onClick={() => void handleLaunchBookBackBatch()}
                disabled={submitting || selectedCategories.size === 0 || !collectionId}
                style={{ width: "100%", padding: "12px 16px", fontSize: "1rem" }}
              >
                {submitting ? "Launching Batch…" : "🚀 Launch Chapter Book-Back Generation"}
              </button>
            </div>

            {/* Recent Chapter History Table */}
            {existingContent.data && existingContent.data.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <h4>Recent Chapter Content Items</h4>
                <div className="grid" style={{ gap: 6 }}>
                  {existingContent.data.slice(0, 5).map((item) => (
                    <div key={item.id} className="row between" style={{ padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 4 }}>
                      <div className="row" style={{ gap: 6, alignItems: "center" }}>
                        <span className="mini"><strong>{item.categoryName}</strong></span>
                        <StatusBadge status={item.status} />
                        {item.missingGuideAnswer && (
                          <span className="badge failed mini" title="Answer missing in Guide PDF">Missing Guide Answer</span>
                        )}
                      </div>
                      <span className="mini muted">{new Date(item.updatedAt).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {toastElement}
    </div>
  );
}
